/**
 * Operator: coupon minting (plan §26, §13.1).
 *
 * Owner-minted promotion codes only — §29.2 keeps amounts out of source, so this form specifies
 * a PERCENTAGE and a duration, never a price. The API resolves it against the configured product.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api } from "../../api/client.js";
import { Banner, Empty, Loading, useCopy } from "../../components/ui.js";
import { AdminError, AdminNav } from "./shared.js";

interface Promotion {
  id?: string;
  code: string;
  percentOff?: number;
  durationMonths?: number | null;
  maxRedemptions?: number | null;
  trialDays?: number | null;
  timesRedeemed?: number;
  active?: boolean;
}

export default function AdminCoupons() {
  const [promotions, setPromotions] = useState<Promotion[] | null>(null);
  const [listError, setListError] = useState<unknown>(null);
  const [formError, setFormError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [created, setCreated] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, copy] = useCopy();

  const [code, setCode] = useState("");
  const [percentOff, setPercentOff] = useState(50);
  const [durationMonths, setDurationMonths] = useState(3);
  const [maxRedemptions, setMaxRedemptions] = useState(100);
  const [trialDays, setTrialDays] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.adminPromotions();
      setPromotions(res.promotions as Promotion[]);
      setListError(null);
    } catch (e) {
      setListError(e);
      setPromotions(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setFormError(null);
    setCreated(null);
    try {
      await api.adminCreateCoupon({
        code: code.trim().toUpperCase(),
        percentOff,
        ...(durationMonths > 0 ? { durationMonths } : {}),
        ...(maxRedemptions > 0 ? { maxRedemptions } : {}),
        ...(trialDays > 0 ? { trialDays } : {}),
      });
      setCreated(code.trim().toUpperCase());
      setCode("");
      await load();
    } catch (err) {
      setFormError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <p className="eyebrow">Operator</p>
        <h1>Coupons</h1>
        <p className="lede">
          Mint a promotion code customers can enter at checkout. Percentages only; prices live in
          configuration, never in a form.
        </p>
      </div>
      <AdminNav />

      {created ? (
        <Banner kind="info" title="Code created">
          <span className="mono">{created}</span>{" "}
          <button type="button" className="btn btn--link btn--sm" onClick={() => copy(created)}>
            {copied ? "Copied" : "Copy"}
          </button>
        </Banner>
      ) : null}
      {formError ? <AdminError error={formError} /> : null}

      <div className="card">
        <h2>New promotion code</h2>
        <form onSubmit={onSubmit}>
          <label className="field">
            <span className="field__label">Code</span>
            <input
              className="input mono"
              type="text"
              required
              maxLength={40}
              placeholder="LAUNCH50"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ""))}
            />
            <span className="field__hint">Upper case letters, digits, hyphen and underscore.</span>
          </label>

          <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
            <label className="field">
              <span className="field__label">Percent off</span>
              <input
                className="input"
                type="number"
                min={1}
                max={100}
                required
                value={percentOff}
                onChange={(e) => setPercentOff(Number(e.target.value))}
              />
            </label>
            <label className="field">
              <span className="field__label">Duration (months)</span>
              <input
                className="input"
                type="number"
                min={0}
                max={36}
                value={durationMonths}
                onChange={(e) => setDurationMonths(Number(e.target.value))}
              />
              <span className="field__hint">0 = forever.</span>
            </label>
            <label className="field">
              <span className="field__label">Max redemptions</span>
              <input
                className="input"
                type="number"
                min={0}
                value={maxRedemptions}
                onChange={(e) => setMaxRedemptions(Number(e.target.value))}
              />
              <span className="field__hint">0 = unlimited.</span>
            </label>
            <label className="field">
              <span className="field__label">Trial days</span>
              <input
                className="input"
                type="number"
                min={0}
                max={90}
                value={trialDays}
                onChange={(e) => setTrialDays(Number(e.target.value))}
              />
            </label>
          </div>

          <div className="btn-row">
            <button type="submit" className="btn btn--primary" disabled={busy || !code.trim()}>
              {busy ? "Creating" : "Mint code"}
            </button>
          </div>
        </form>
      </div>

      <h2 style={{ marginTop: 40, marginBottom: 16 }}>Existing codes</h2>
      {loading ? <Loading label="Loading codes" /> : null}
      {listError ? <AdminError error={listError} onRetry={() => void load()} /> : null}
      {!loading && promotions && promotions.length === 0 ? (
        <Empty title="No promotion codes yet">
          <p>Codes you mint will be listed here with their redemption counts.</p>
        </Empty>
      ) : null}
      {!loading && promotions && promotions.length > 0 ? (
        <div className="card table-scroll" style={{ padding: 0 }}>
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Code</th>
                <th scope="col">Off</th>
                <th scope="col">Duration</th>
                <th scope="col">Redeemed</th>
                <th scope="col">Active</th>
              </tr>
            </thead>
            <tbody>
              {promotions.map((p) => (
                <tr key={p.id ?? p.code}>
                  <td className="mono small">{p.code}</td>
                  <td className="muted small">{p.percentOff != null ? `${p.percentOff}%` : "—"}</td>
                  <td className="muted small">{p.durationMonths ? `${p.durationMonths} mo` : "forever"}</td>
                  <td className="muted small">
                    {p.timesRedeemed ?? 0}
                    {p.maxRedemptions ? ` / ${p.maxRedemptions}` : ""}
                  </td>
                  <td>
                    <span className={`pill ${p.active === false ? "pill--idle" : "pill--ok"}`}>
                      {p.active === false ? "Inactive" : "Active"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}
